<template>
  <!-- Step 6 completed state — cloned from the settlement prototype (v8). -->
  <div data-cy="settlement-complete-panel">
    <!-- Negative net proceeds warning (client net recovery <= 0) -->
    <AlertBar
      v-if="negativeNet"
      type="error"
      class="mb-4"
      data-cy="disb-negative-warning"
    >
      <div class="cp-warn-title">Client Net Proceeds Are Negative</div>
      <p class="cp-warn-body">
        Fees, expenses, and liens exceed the settlement amount. Please review the
        settlement breakdown before distributing funds.
      </p>
    </AlertBar>

    <div class="cp-card">
      <!-- ── Completion hero ─────────────────────────── -->
      <div class="done-hero">
        <div class="done-check" aria-hidden="true">
          <svg viewBox="0 0 24 24" class="done-ic">
            <path
              d="M20 6L9 17l-5-5"
              fill="none"
              stroke="#fff"
              stroke-width="2.6"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </div>
        <div class="done-hero-main">
          <div>
            <div class="done-title">Settlement Complete</div>
            <div v-if="generatedDateFormatted" class="done-sub">
              Generated {{ generatedDateFormatted }}
            </div>
          </div>
          <div class="done-hero-actions">
            <LlSecondaryButton
              v-if="canReset"
              variant="text"
              color="error"
              size="small"
              class="cp-unlock"
              prepend-icon="mdi-lock-open-outline"
              data-cy="unlock-reset-button"
              @click="$emit('unlock-and-reset')"
            >
              Unlock &amp; Reset
            </LlSecondaryButton>
            <LlTooltip v-else location="bottom">
              <div class="cp-unlock-tooltip">
                {{ resetBlockedReason || 'Reset is not available right now.' }}
              </div>
              <template #activator>
                <span>
                  <LlSecondaryButton
                    variant="text"
                    size="small"
                    class="cp-unlock"
                    prepend-icon="mdi-lock"
                    disabled
                    data-cy="unlock-reset-button-blocked"
                  >
                    Unlock &amp; Reset
                  </LlSecondaryButton>
                </span>
              </template>
            </LlTooltip>
          </div>
        </div>
      </div>

      <!-- ── Disbursements table ─────────────────────── -->
      <SettlementDisbursementTable />

      <!-- ── Documents ───────────────────────────────── -->
      <div class="psa-docs">
        <a
          class="psa-doc"
          :class="{ 'psa-doc--disabled': !downloadUrl }"
          :href="downloadUrl || undefined"
          download="settlement-statement.pdf"
          data-cy="settlement-statement-link"
          @click="downloadUrl && $emit('download')"
        >
          <span class="icon icon-doc" aria-hidden="true">
            <svg viewBox="0 0 24 24"><path d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zM13 9V3.5L18.5 9H13z" /></svg>
          </span>
          Settlement Statement
        </a>
        <!-- View Invoice — parity with the pre-redesign flow (SettlementPostActions),
             which links here once the invoice is finalized. The disbursement-table
             path had no equivalent, so this closed silently rather than by removal. -->
        <a
          v-if="invoiceFinalized"
          class="psa-doc"
          data-cy="view-invoice-link"
          @click.prevent="$emit('view-invoice')"
        >
          <span class="icon icon-doc" aria-hidden="true">
            <svg viewBox="0 0 24 24"><path d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zM13 9V3.5L18.5 9H13z" /></svg>
          </span>
          View Invoice
        </a>
        <a
          v-if="invoiceFinalized"
          class="psa-doc"
          :class="{ 'psa-doc--disabled': trustStatementLoading }"
          data-cy="trust-statement-link"
          @click.prevent="!trustStatementLoading && $emit('download-trust-statement')"
        >
          <span class="icon icon-doc" aria-hidden="true">
            <svg viewBox="0 0 24 24"><path d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zM13 9V3.5L18.5 9H13z" /></svg>
          </span>
          {{ trustStatementLoading ? 'Downloading Trust Statement…' : 'Trust Statement' }}
          <LlTooltip location="top">
            <div style="max-width: 260px">
              A summary of all trust account activity associated with this
              settlement: deposits, disbursements, lien payoffs, and payments.
            </div>
            <template #activator>
              <span class="info-dot" tabindex="0">i</span>
            </template>
          </LlTooltip>
        </a>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { storeToRefs } from 'pinia';
import AlertBar from '@/components/AlertBar.vue';
import LlSecondaryButton from '@/components/LlSecondaryButton.vue';
import LlTooltip from '@/components/LlTooltip.vue';
import { formatLongDateString } from '@/services/utils';
import { useSettlementStore } from '@/modules/matters/settlement/stores/useSettlementStore';
import SettlementDisbursementTable from './SettlementDisbursementTable.vue';

const props = defineProps({
  generatedDate: { type: String, default: null },
  downloadUrl: { type: String, default: null },
  canReset: { type: Boolean, default: true },
  resetBlockedReason: { type: String, default: null },
  invoiceFinalized: { type: Boolean, default: false },
  trustStatementLoading: { type: Boolean, default: false },
});

defineEmits(['unlock-and-reset', 'download', 'download-trust-statement', 'view-invoice']);

const { disbursements } = storeToRefs(useSettlementStore());
const negativeNet = computed(() => !!disbursements.value.data?.clientNetNegative);

const generatedDateFormatted = computed(() =>
  props.generatedDate
    ? formatLongDateString(props.generatedDate) || props.generatedDate
    : '',
);
</script>

<style scoped>
/* Cloned from the settlement prototype (v8). */
.cp-card {
  /* Brand color in one place (swap for a Vuetify theme token later). */
  --ll-green: #29bf12;
  background: #fff;
  border: 1px solid #e5e5e5;
  border-radius: 6px;
  overflow: hidden;
}

.cp-warn-title {
  font-weight: 700;
  margin-bottom: 2px;
}
.cp-warn-body {
  font-size: 12px;
  line-height: 1.5;
  margin: 0;
}

/* Completion hero */
.done-hero {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 24px 26px;
  background: linear-gradient(180deg, #f1faf4 0%, #ffffff 100%);
  border-bottom: 1px solid #e5e5e5;
  flex-wrap: wrap;
}
.done-check {
  width: 46px;
  height: 46px;
  border-radius: 999px;
  background: var(--ll-green);
  display: grid;
  place-items: center;
  box-shadow: 0 3px 12px rgba(41, 191, 18, 0.45);
  flex: 0 0 auto;
}
.done-ic {
  width: 24px;
  height: 24px;
}
.done-hero-main {
  flex: 1;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px;
}
.done-title {
  font-size: 19px;
  font-weight: 700;
  color: #424242;
  letter-spacing: -0.01em;
}
.done-sub {
  font-size: 12.5px;
  color: #747474;
  margin-top: 3px;
}
.done-hero-actions {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  flex-shrink: 0;
}
.cp-unlock {
  text-transform: none;
  letter-spacing: 0;
}
.cp-unlock-tooltip {
  max-width: 260px;
}

/* Documents */
.psa-docs {
  display: flex;
  gap: 10px;
  padding: 16px 26px 22px;
  border-top: 1px solid #eee;
  flex-wrap: wrap;
}
.psa-doc {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  font-size: 12.5px;
  font-weight: 600;
  color: #424242;
  border: 1px solid #e5e5e5;
  border-radius: 3px;
  padding: 8px 13px;
  cursor: pointer;
  text-decoration: none;
}
.psa-doc:hover {
  background: #f7f7f7;
  border-color: #cfd4d9;
  text-decoration: none;
}
.psa-doc--disabled {
  opacity: 0.6;
  pointer-events: none;
}
.psa-doc .icon {
  width: 15px;
  height: 15px;
  display: inline-flex;
}
.psa-doc .icon svg {
  width: 100%;
  height: 100%;
  fill: #666;
}

.info-dot {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 1.5px solid #90a4ae;
  color: #90a4ae;
  font-size: 10px;
  font-weight: 700;
  cursor: help;
  vertical-align: middle;
  margin-left: 4px;
  font-style: normal;
  line-height: 1;
}
.info-dot:hover,
.info-dot:focus {
  border-color: var(--ll-green);
  color: var(--ll-green);
  outline: none;
}
</style>
